import { Injectable } from '@angular/core';
import { BaseSelectStatementId } from 'src/app/enums/base-select-statement-id.enum';
import { SelectStatementOptions } from 'src/app/models/select-statement-options.model';
import { TableDataOperator } from 'src/app/operators/table-data.operator';
import { Observable, firstValueFrom, map } from 'rxjs';
import { SelectStatementResult } from 'src/app/models/select-statement-result.model';
import { RolePermission } from 'src/app/services/auth.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { Route } from '@angular/router';
import { NavbarItem } from '../models/navbar-item.model';
import { BaseService } from 'src/app/services/base.service';

@Injectable({
	providedIn: 'root'
})
export class NavbarService {

	public navbarItems: NavbarItem[] = [];

	public navbarItemsTableDataOperator: TableDataOperator<NavbarItem> = new TableDataOperator<NavbarItem>(
		new SelectStatementOptions(
			{
				baseSelectStatementId: BaseSelectStatementId.DEV_GetNavbarNavigationItems
			}
		),
		NavbarItem
	);

	public visibleNavbarItems: NavbarItem[] = [];

	public findNavbarItemIfVisible(route: Route): NavbarItem {

		const findRecursively = (navbarItems: NavbarItem[], depth = 0): NavbarItem => {

			if (depth > 20) { // Add reasonable depth limit
				console.warn('Maximum recursion depth reached - findRecursively');
				 
			}

			for (let a = 0; a < navbarItems.length; a++) {
				let navbarItem = navbarItems[a];

				if (navbarItem.routeId === route.data.id) {
					return navbarItem;
				}

				if (navbarItem.children) {
					navbarItem = findRecursively(navbarItem.children);

					if (navbarItem !== null) {
						return navbarItem;
					}
				}
			}

			return null;
		}

		return findRecursively(this.visibleNavbarItems);
	}

	findNavbarItemIfHidden(routeConfig: Route): boolean {
		try {
			if (this.allUrlRawData) {
				return this.allUrlRawData.find(x => x.routeId == routeConfig.data.id).isActive;
			}
			return true;
		} catch (errtrueor) {
			// Handle the error here
			//console.info('An error occurred:', error);
		}
		return true;
	}

	public initializeVisibleNavbarItems(): void {
		// We don't want to show `NavbarItems` that aren't accessible to the current user, so we take only visible items.
		const rolePermissions: RolePermission[] = this._localStorageService.get<RolePermission[]>(RolePermission.storage_key);

		this.visibleNavbarItems = [];

		const isVisible: (navbarItem: NavbarItem) => boolean = (navbarItem: NavbarItem) => {
			if (rolePermissions != undefined && rolePermissions != null) {
				let rolePermission: RolePermission = rolePermissions.find((rolePermission) => rolePermission.menuId === navbarItem.id);
				return rolePermission && rolePermission.isVisible;
			}
			return false;
		}

		const addIfVisibleRecursively: (navbarItem: NavbarItem, navbarItemParent: NavbarItem) => boolean = (navbarItem: NavbarItem, navbarItemParent: NavbarItem, depth = 0) => {
			if (depth > 20) {
				console.warn('Maximum recursion depth reached in navbar processing : addIfVisibleRecursively');
				 
			}
			if (isVisible(navbarItem)) {

				const children: NavbarItem[] = navbarItem.children;

				navbarItem = Object.assign(new NavbarItem(navbarItem), { children: [] });

				if (navbarItemParent) {
					navbarItemParent.children.push(navbarItem);
				} else {
					this.visibleNavbarItems.push(navbarItem);
				}

				for (let a = 0; a < children.length; a++) {
					const navbarItemChild = children[a];

					addIfVisibleRecursively(navbarItemChild, navbarItem);
				}

				return true;
			}

			return false;
		}

		for (let a = 0; a < this.navbarItems.length; a++) {
			const navbarItem: NavbarItem = this.navbarItems[a];

			addIfVisibleRecursively(navbarItem, null);
		}
	}

	public allUrlRawData: any;
	public async loadNavbarItems(...staticNavbarItems: NavbarItem[]): Promise<void> {
		if (!this.baseService.checkSession()) {
			return Promise.resolve(null);
		}
		else {
			const observable$: Observable<SelectStatementResult<NavbarItem>> = await this.navbarItemsTableDataOperator.getObservable(
				{
					name: "Navbar Items Request",
					responseLoggingEnabled: false,
					showLoadingIndicator: false,
				}
			);

			await firstValueFrom(
				observable$.pipe(
					map(
						(selectStatementResult: SelectStatementResult<NavbarItem>) => {
							selectStatementResult.data.push(...staticNavbarItems);

							selectStatementResult.data.sort(
								(a: NavbarItem, b: NavbarItem) => a.sortingPriority - b.sortingPriority
							);
							this.allUrlRawData = [...selectStatementResult.data];
							selectStatementResult.data = selectStatementResult.data.filter(x => x.isActive == true);
							this.navbarItems = [];

							const populateChildren: (navbarItem: NavbarItem) => void = (navbarItem: NavbarItem) => {
								navbarItem.children = [];
								navbarItem.children.push(...selectStatementResult.data.filter((item) => item.parentId && navbarItem.id && item.parentId === navbarItem.id));

								for (let a = 0; a < navbarItem.children.length; a++) {
									populateChildren(navbarItem.children[a]);
								}

								if (navbarItem.children.length > 0) {
									navbarItem.children[navbarItem.children.length - 1].isRelativelyLast = true;
								}
							};

							for (let a = 0; a < selectStatementResult.data.length; a++) {
								const navbarItem: NavbarItem = selectStatementResult.data[a];

								if (typeof navbarItem.parentId === 'undefined' || navbarItem.parentId === null) {
									populateChildren(navbarItem);

									this.navbarItems.push(navbarItem);
								}
							}

							if (selectStatementResult.data.length > 0) {
								selectStatementResult.data[selectStatementResult.data.length - 1].isRelativelyLast = true;
							}

							this.initializeVisibleNavbarItems();

							return this.navbarItems;
						}
					)
				)
			);
		}
	}

	constructor(
		private _localStorageService: LocalStorageService,
		private baseService: BaseService
		// private _routeConfigService: RouteConfigService,
	) {
		// When role permissions change we track that change and apply changes to navbar right away.
		this._localStorageService.track(RolePermission.storage_key).subscribe(
			(_) => {

				this.initializeVisibleNavbarItems();
			}
		);
	}
}
